import React, { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

// Watches the service worker for a newly deployed ("waiting") version and shows
// a dismissible bottom banner. Tapping Update activates the new SW immediately
// and reloads to the latest build. Dismiss hides it for now; it reappears next
// app open (the new worker stays "waiting") until the user updates.
export default function UpdateBanner() {
  const [waiting, setWaiting] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const promote = (worker) => {
      // Only prompt when there's already a controller (i.e. not the first install).
      if (worker && navigator.serviceWorker.controller) setWaiting(worker);
    };

    let reg;
    navigator.serviceWorker.getRegistration().then((r) => {
      if (!r) return;
      reg = r;
      if (r.waiting) promote(r.waiting);
      r.addEventListener("updatefound", () => {
        const nw = r.installing;
        if (!nw) return;
        nw.addEventListener("statechange", () => {
          if (nw.state === "installed") promote(nw);
        });
      });
    });

    let refreshing = false;
    const onControllerChange = () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    // Periodically check the server for a fresh worker.
    const iv = setInterval(() => {
      navigator.serviceWorker.getRegistration().then((r) => r && r.update().catch(() => {}));
    }, 60000);

    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      clearInterval(iv);
    };
  }, []);

  const update = () => {
    if (waiting) waiting.postMessage("SKIP_WAITING");
    // controllerchange handler reloads once the new worker takes over.
  };

  if (!waiting || dismissed) return null;

  return (
    <div
      data-testid="pwa-update-banner"
      className="fixed inset-x-0 bottom-0 z-[300] flex justify-center px-3 pb-3 pointer-events-none"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.75rem)" }}
    >
      <div className="pointer-events-auto w-full max-w-md bg-[#181427] text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 animate-rise">
        <div className="w-9 h-9 rounded-full bg-[#E8614A]/20 flex items-center justify-center shrink-0">
          <RefreshCw className="w-4 h-4 text-[#E8614A]" />
        </div>
        <p className="flex-1 min-w-0 text-sm leading-snug">A new version is available, tap to update</p>
        <button
          data-testid="pwa-update-btn"
          onClick={update}
          className="shrink-0 bg-[#E8614A] hover:bg-[#d8543c] text-white text-sm font-semibold rounded-full px-4 py-2 transition-colors"
        >
          Update
        </button>
        <button
          data-testid="pwa-update-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Update later"
          className="shrink-0 text-white/50 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
